import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate, Easing } from "remotion";
import type {
  OverlayManifestData, Overlay,
  YearStampData, PersonTagData, LocationTagData, ScriptureRefData,
  StatCardData, InfoBoxData, QuoteCardData,
  MeasurementBracketData, PathTraceData, FlowArrowData, LikeSubscribeData, MantraCardData,
} from "./types";
import { YearStamp } from "./YearStamp";
import { PersonTag } from "./PersonTag";
import { LocationTag } from "./LocationTag";
import { ScriptureRef } from "./ScriptureRef";
import { StatCard } from "./StatCard";
import { InfoBoxC } from "./InfoBoxC";
import { QuoteCardA } from "./QuoteCardA";
import { MeasurementBracket } from "./MeasurementBracket";
import { PathTrace } from "./PathTrace";
import { FlowArrow } from "./FlowArrow";
import { LikeSubscribeRouter } from "./LikeSubscribeRouter";
import { MantraCard } from "./MantraCard";
import { MantraCardBadrinath1 } from "./MantraCardBadrinath1";
import { HarHarMahadev } from "./HarHarMahadev";
import { JaiSomnathCTA } from "./JaiSomnathCTA";
import { HarHarGangeCTA } from "./HarHarGangeCTA";

const LS_DURATION_FRAMES = 96;
const SLIDE_FRAMES = 10;
const DEFAULT_TAG_DURATION_MS = 5000;
const QUOTE_SHIFT_PX = 60;
const INFO_TOP_DEFAULT = 25;
const INFO_TOP_WITH_YEAR = 117;
const TAG_BASE_BOTTOM = 30;
const TAG_SLOT_H = 40;

// SlotWrapper — stacks year_stamp, scripture_ref, stat_card
const STACK_CONFIG: Record<string, { direction: "up" | "down"; slotPx: number }> = {
  year_stamp:    { direction: "down", slotPx: 64 },
  scripture_ref: { direction: "down", slotPx: 36 },
  stat_card:     { direction: "up",   slotPx: 64 },
};

interface Scheduled {
  overlay: Overlay;
  startMs: number;
  endMs: number;
  startFrame: number;
  endFrame: number;
}

const SlotWrapper: React.FC<{
  myIndex: number;
  all: Scheduled[];
  seqStart: number;
  children: React.ReactNode;
  overlayType: string;
}> = ({ myIndex, all, seqStart, children, overlayType }) => {
  const localFrame = useCurrentFrame();
  const gf = localFrame + seqStart;
  const cfg = STACK_CONFIG[overlayType];
  if (!cfg) return <>{children}</>;

  const earlier = all.slice(0, myIndex).filter(s => s.overlay.type === overlayType);
  let animSlot = 0;
  for (const e of earlier) {
    if (gf < e.startFrame) continue;
    if (gf < e.endFrame) {
      animSlot += 1;
    } else {
      animSlot += 1 - Math.min((gf - e.endFrame) / SLIDE_FRAMES, 1);
    }
  }
  const offsetPx = cfg.direction === "up" ? -animSlot * cfg.slotPx : animSlot * cfg.slotPx;
  return (
    <div style={{ transform: `translateY(${offsetPx}px)`, willChange: "transform" }}>
      {children}
    </div>
  );
};

// QuoteShiftWrapper — shifts person/location tags up when a quote card is active
const QuoteShiftWrapper: React.FC<{
  children: React.ReactNode;
  quoteIntervals: Array<{ startFrame: number; endFrame: number }>;
}> = ({ children, quoteIntervals }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const shiftDur = Math.round(fps);

  let totalOffset = 0;
  for (const qi of quoteIntervals) {
    const shiftIn = interpolate(frame, [qi.startFrame, qi.startFrame + shiftDur], [0, QUOTE_SHIFT_PX], {
      extrapolateLeft: "clamp", extrapolateRight: "clamp",
      easing: Easing.bezier(0.16, 1, 0.3, 1),
    });
    const shiftOut = interpolate(frame, [qi.endFrame - shiftDur, qi.endFrame], [0, -QUOTE_SHIFT_PX], {
      extrapolateLeft: "clamp", extrapolateRight: "clamp",
      easing: Easing.bezier(0.7, 0, 0.84, 0),
    });
    totalOffset += shiftIn + shiftOut;
  }

  return (
    <div style={{ position: "absolute", inset: 0, transform: `translateY(${-totalOffset}px)`, willChange: "transform" }}>
      {children}
    </div>
  );
};

type TopConfig = { mode: "static"; top: number } | { mode: "animate"; yearStampStartFrame: number };

// DynamicInfoBoxC — computes animated top position relative to YearStamp presence
const DynamicInfoBoxC: React.FC<{
  data: InfoBoxData;
  durationInFrames: number;
  startFrame: number;
  topConfig: TopConfig;
}> = ({ data, durationInFrames, startFrame, topConfig }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const top = topConfig.mode === "static"
    ? topConfig.top
    : interpolate(frame,
        [topConfig.yearStampStartFrame, topConfig.yearStampStartFrame + Math.round(1.5 * fps)],
        [INFO_TOP_DEFAULT, INFO_TOP_WITH_YEAR],
        { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.4, 0, 0.2, 1) }
      );

  return <InfoBoxC data={data} durationInFrames={durationInFrames} startFrame={startFrame} top={top} />;
};

function renderSimpleOverlay(overlay: Overlay, durationFrames: number): React.ReactNode {
  switch (overlay.type) {
    case "year_stamp":
      return <YearStamp data={overlay.data as YearStampData} durationInFrames={durationFrames} />;
    case "scripture_ref":
      return <ScriptureRef data={overlay.data as ScriptureRefData} durationInFrames={durationFrames} />;
    case "stat_card":
      return <StatCard data={overlay.data as StatCardData} durationInFrames={durationFrames} />;
    case "quote_card":
      return <QuoteCardA data={overlay.data as QuoteCardData} durationInFrames={durationFrames} />;
    case "measurement_bracket":
      return <MeasurementBracket data={overlay.data as MeasurementBracketData} durationInFrames={durationFrames} />;
    case "path_trace":
      return <PathTrace data={overlay.data as PathTraceData} durationInFrames={durationFrames} />;
    case "flow_arrow":
      return <FlowArrow data={overlay.data as FlowArrowData} durationInFrames={durationFrames} />;
    case "like_subscribe":
      return <LikeSubscribeRouter variant={(overlay.data as LikeSubscribeData).variant} />;
    case "mantra_card":
      return <MantraCard data={overlay.data as MantraCardData} durationInFrames={durationFrames} />;
    case "mantra_card_badrinath_1":
      return <MantraCardBadrinath1 data={overlay.data as MantraCardData} durationInFrames={durationFrames} />;
    case "har_har_mahadev_cta":
      return <HarHarMahadev />;
    case "jai_somnath_cta":
      return <JaiSomnathCTA durationInFrames={durationFrames} />;
    case "har_har_gange_cta":
      return <HarHarGangeCTA durationInFrames={durationFrames} />;
    default:
      return null;
  }
}

export const OverlayManifest: React.FC<{ manifest: OverlayManifestData }> = ({ manifest }) => {
  const { fps } = useVideoConfig();

  // ── 1. Rolling group end for person + location tags ────────────────────────
  // Each tag extends the group deadline to (its start + 5s) if it arrives before expiry.
  // Tags arriving after the group expires start a fresh group.
  const tagOverlays = [...manifest.overlays]
    .filter(o => o.type === "person_tag" || o.type === "location_tag")
    .sort((a, b) => a.start_ms - b.start_ms);

  const tagGroupEndMs = new Map<Overlay, number>();
  let groupBucket: Overlay[] = [];
  let groupDeadline = -Infinity;

  for (const tag of tagOverlays) {
    if (tag.start_ms >= groupDeadline && groupBucket.length > 0) {
      // Arrived after current group expired → finalize bucket
      groupBucket.forEach(t => tagGroupEndMs.set(t, groupDeadline));
      groupBucket = [];
    }
    groupDeadline = Math.max(groupDeadline, tag.start_ms + DEFAULT_TAG_DURATION_MS);
    groupBucket.push(tag);
  }
  // Finalize last bucket
  groupBucket.forEach(t => tagGroupEndMs.set(t, groupDeadline));

  // ── 2. Quote card intervals ─────────────────────────────────────────────────
  const quoteIntervals = manifest.overlays
    .filter(o => o.type === "quote_card")
    .map(o => ({
      startFrame: Math.round((o.start_ms / 1000) * fps),
      endFrame: Math.round((o.end_ms / 1000) * fps),
    }));

  // ── 3. YearStamp effective end (syncs to overlapping InfoBox end) ───────────
  const yearStampEffectiveEndFrame = new Map<Overlay, number>();
  for (const ys of manifest.overlays.filter(o => o.type === "year_stamp")) {
    let eff = Math.round((ys.end_ms / 1000) * fps);
    for (const ib of manifest.overlays.filter(o => o.type === "info_box")) {
      if (ib.start_ms >= ys.start_ms && ib.start_ms < ys.end_ms) {
        eff = Math.max(eff, Math.round((ib.end_ms / 1000) * fps));
      }
    }
    yearStampEffectiveEndFrame.set(ys, eff);
  }

  // ── 4. InfoBox top config ───────────────────────────────────────────────────
  const infoBoxTopConfig = new Map<Overlay, TopConfig>();
  for (const ib of manifest.overlays.filter(o => o.type === "info_box")) {
    let config: TopConfig = { mode: "static", top: INFO_TOP_DEFAULT };
    let earliestYearAfter: number | null = null;

    for (const ys of manifest.overlays.filter(o => o.type === "year_stamp")) {
      if (ys.start_ms <= ib.start_ms && ys.end_ms > ib.start_ms) {
        // YearStamp already active when InfoBox appears → static 117
        config = { mode: "static", top: INFO_TOP_WITH_YEAR };
        earliestYearAfter = null;
        break;
      } else if (ys.start_ms > ib.start_ms && ys.start_ms < ib.end_ms) {
        // YearStamp arrives while InfoBox showing → track earliest
        if (earliestYearAfter === null || ys.start_ms < earliestYearAfter) {
          earliestYearAfter = ys.start_ms;
        }
      }
    }

    if (earliestYearAfter !== null) {
      config = { mode: "animate", yearStampStartFrame: Math.round((earliestYearAfter / 1000) * fps) };
    }

    infoBoxTopConfig.set(ib, config);
  }

  // ── 5. Tag slot indices — reset per rolling group ──────────────────────────
  const tagSlots = new Map<Overlay, number>();
  // Group tags by their groupEndMs, then assign slots within each group
  const groupBuckets = new Map<number, { persons: Overlay[]; locations: Overlay[] }>();
  for (const o of manifest.overlays) {
    if (o.type !== "person_tag" && o.type !== "location_tag") continue;
    const gEnd = tagGroupEndMs.get(o) ?? 0;
    if (!groupBuckets.has(gEnd)) groupBuckets.set(gEnd, { persons: [], locations: [] });
    const bucket = groupBuckets.get(gEnd)!;
    if (o.type === "person_tag")   { tagSlots.set(o, bucket.persons.length);   bucket.persons.push(o); }
    if (o.type === "location_tag") { tagSlots.set(o, bucket.locations.length); bucket.locations.push(o); }
  }

  // ── 6. Build scheduled list with effective end frames ──────────────────────
  const scheduled: Scheduled[] = manifest.overlays.map(overlay => {
    const startFrame = Math.round((overlay.start_ms / 1000) * fps);
    let endFrame = Math.round((overlay.end_ms / 1000) * fps);

    if (overlay.type === "person_tag" || overlay.type === "location_tag") {
      const gEnd = tagGroupEndMs.get(overlay);
      if (gEnd !== undefined) endFrame = Math.round((gEnd / 1000) * fps);
    } else if (overlay.type === "year_stamp") {
      endFrame = yearStampEffectiveEndFrame.get(overlay) ?? endFrame;
    }

    return { overlay, startMs: overlay.start_ms, endMs: overlay.end_ms, startFrame, endFrame };
  });

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <AbsoluteFill>
      {scheduled.map((s, i) => {
        const durationFrames = s.overlay.type === "like_subscribe"
          ? LS_DURATION_FRAMES
          : Math.max(s.endFrame - s.startFrame, 4);

        // person_tag — no Sequence, uses startFrame prop + QuoteShiftWrapper
        if (s.overlay.type === "person_tag") {
          const slot = tagSlots.get(s.overlay) ?? 0;
          return (
            <QuoteShiftWrapper key={i} quoteIntervals={quoteIntervals}>
              <PersonTag
                data={s.overlay.data as PersonTagData}
                durationInFrames={durationFrames}
                startFrame={s.startFrame}
                bottom={TAG_BASE_BOTTOM + slot * TAG_SLOT_H}
              />
            </QuoteShiftWrapper>
          );
        }

        // location_tag — same pattern
        if (s.overlay.type === "location_tag") {
          const slot = tagSlots.get(s.overlay) ?? 0;
          return (
            <QuoteShiftWrapper key={i} quoteIntervals={quoteIntervals}>
              <LocationTag
                data={s.overlay.data as LocationTagData}
                durationInFrames={durationFrames}
                startFrame={s.startFrame}
                bottom={TAG_BASE_BOTTOM + slot * TAG_SLOT_H}
              />
            </QuoteShiftWrapper>
          );
        }

        // info_box — DynamicInfoBoxC with animated top, no Sequence
        if (s.overlay.type === "info_box") {
          const topConfig = infoBoxTopConfig.get(s.overlay) ?? { mode: "static" as const, top: INFO_TOP_DEFAULT };
          return (
            <DynamicInfoBoxC
              key={i}
              data={s.overlay.data as InfoBoxData}
              durationInFrames={durationFrames}
              startFrame={s.startFrame}
              topConfig={topConfig}
            />
          );
        }

        // All other types — Sequence + SlotWrapper
        const child = renderSimpleOverlay(s.overlay, durationFrames);
        if (!child) return null;

        return (
          <Sequence key={i} from={s.startFrame} durationInFrames={durationFrames} layout="none">
            <SlotWrapper myIndex={i} all={scheduled} seqStart={s.startFrame} overlayType={s.overlay.type}>
              {child}
            </SlotWrapper>
          </Sequence>
        );
      })}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
